import { DEFAULT_DATA_POINT_RADIUS } from '../../core/constants.js';
import { getClusterBoundariesAtPosition } from '../../core/utils/binary-search.js';
import { safeGet } from '../../core/utils/safe-get.js';
import type {
  BarSeriesQueryResult,
  HoverQuery,
  HoverQueryArgs,
  HoverQueryResult,
  SeriesQueryResult,
} from '../context/XYChartOverlayPerformance.context.js';
import {
  type AreaSeriesWithDatapoints,
  BAR_VARIANT,
  type BarSeriesWithDatapoints,
  type LineSeriesWithDatapoints,
  type MapDatapoints,
} from '../types/xy-chart-internals.js';
import type {
  AreaDatapoint,
  BarDatapoint,
  LineDatapoint,
} from '../types/xy-chart.js';
import type {
  GetBarPixelCenter,
  GetLinearSeriesPixelCenter,
  SortedEntriesPerShape,
  SortedEntry,
} from './build-sorted-entries.js';
import type { QueryAllHovered } from './query-hovered.js';

export function buildQueryShared(
  sortedEntries: SortedEntriesPerShape | undefined,
  queryAllHovered: QueryAllHovered,
  getBarPixelCenter: GetBarPixelCenter,
  getLinearSeriesPixelCenter: GetLinearSeriesPixelCenter,
  datapointMetadataMap: MapDatapoints,
  hasGroupedBars: boolean,
): HoverQuery {
  // Grouped (side-by-side) bars share a domain range but sit at different
  // pixel positions, so we cluster by `boundaries` to keep the whole
  // category together. Otherwise we cluster by rounded baseline pixel to
  // preserve the default XY behavior for stacked bars and cross-shape
  // overlaps.
  const getClusterKey = hasGroupedBars
    ? (e: SortedEntry) => e.boundaries
    : (e: SortedEntry) => Math.round(e.baselinePixel);

  return function queryShared({
    position,
    rectSeriesData,
    barSeriesData,
    lineSeriesData,
    areaSeriesData,
  }: HoverQueryArgs): HoverQueryResult {
    const relative = position.relative;

    const allRectsHovered = queryAllHovered(rectSeriesData, relative);
    if (allRectsHovered.length > 0) {
      return {
        preferred: allRectsHovered.at(-1),
        datapoints: allRectsHovered,
      };
    }

    const allBarsHovered = queryAllHovered(
      barSeriesData,
      relative,
    ) as BarSeriesQueryResult[];

    const lastHovered = allBarsHovered.at(-1);
    const lastHoveredCenter = lastHovered
      ? getBarPixelCenter(lastHovered.series, lastHovered.datapoint)
      : undefined;
    const firstBarSeries = barSeriesData[0];
    const firstBarDatapoint = firstBarSeries?.data[0];
    const isHorizontal =
      lastHoveredCenter?.isHorizontal ??
      (firstBarSeries !== undefined && firstBarDatapoint !== undefined
        ? getBarPixelCenter(firstBarSeries, firstBarDatapoint).isHorizontal
        : false);
    const searchBaselinePixel =
      lastHoveredCenter?.baselinePixel ??
      (isHorizontal ? relative.y : relative.x);

    // The sorted-entry build is debounced and `undefined` during the
    // first few frames of a render. Fall back to a linear scan over all
    // series so the tooltip works immediately on first hover.
    const clusterEntries = sortedEntries
      ? findClosestClusterBinary(
          sortedEntries.combined,
          searchBaselinePixel,
          getClusterKey,
        )
      : filterToClosestCluster(
          collectLinearEntries(
            barSeriesData,
            lineSeriesData,
            areaSeriesData,
            datapointMetadataMap,
            getBarPixelCenter,
            getLinearSeriesPixelCenter,
          ),
          searchBaselinePixel,
          getClusterKey,
        );

    if (clusterEntries.length === 0) {
      return { preferred: undefined, datapoints: [] };
    }

    if (
      clusterEntries.some((e) => !datapointMetadataMap.has(e.result.datapoint))
    ) {
      return { preferred: undefined, datapoints: [] };
    }

    const topmostHovered = allBarsHovered.at(-1);

    // For vertical charts the cursor discriminates stacked bars by Y; for
    // horizontal charts it discriminates by X (the value axis).
    const cursorValue = clusterEntries[0].isHorizontal
      ? relative.x
      : relative.y;

    const distanceToCursor = (entry: SortedEntry): number =>
      Math.min(
        Math.abs(entry.value0Pixel - cursorValue),
        Math.abs(entry.value1Pixel - cursorValue),
      );

    const euclideanDistanceToCursor = (entry: SortedEntry): number => {
      const [cursorDomain, cursorVal] = entry.isHorizontal
        ? [relative.y, relative.x]
        : [relative.x, relative.y];
      return Math.sqrt(
        Math.pow(entry.baselinePixel - cursorDomain, 2) +
          Math.pow(entry.value0Pixel - cursorVal, 2),
      );
    };

    const deltasCache = new WeakMap<
      SortedEntry,
      { baselineDelta: number; valueDelta: number }
    >();
    const groupedBarAxisDeltas = (
      entry: SortedEntry,
    ): { baselineDelta: number; valueDelta: number } => {
      const cached = deltasCache.get(entry);
      if (cached !== undefined) {
        return cached;
      }
      const [cursorDomain, cursorVal] = entry.isHorizontal
        ? [relative.y, relative.x]
        : [relative.x, relative.y];
      const valueSpanStart = Math.min(entry.value0Pixel, entry.value1Pixel);
      const valueSpanEnd = Math.max(entry.value0Pixel, entry.value1Pixel);
      const valueDelta = Math.max(
        valueSpanStart - cursorVal,
        cursorVal - valueSpanEnd,
        0,
      );
      const baselineDelta = Math.abs(entry.baselinePixel - cursorDomain);
      const result = { baselineDelta, valueDelta };
      deltasCache.set(entry, result);
      return result;
    };

    const compareGroupedBarDistance = (
      a: SortedEntry,
      b: SortedEntry,
    ): number => {
      const deltaA = groupedBarAxisDeltas(a);
      const deltaB = groupedBarAxisDeltas(b);
      if (deltaA.baselineDelta !== deltaB.baselineDelta) {
        return deltaA.baselineDelta - deltaB.baselineDelta;
      }
      const valueA = Number.isNaN(deltaA.valueDelta)
        ? Infinity
        : deltaA.valueDelta;
      const valueB = Number.isNaN(deltaB.valueDelta)
        ? Infinity
        : deltaB.valueDelta;
      return valueA - valueB;
    };

    const compareEntries = hasGroupedBars
      ? compareGroupedBarDistance
      : (a: SortedEntry, b: SortedEntry) =>
          distanceToCursor(a) - distanceToCursor(b);

    const closestEntry = clusterEntries.reduce<SortedEntry | undefined>(
      (closest, entry) => {
        if (closest === undefined) {
          return entry;
        }
        if (
          entry.result.type === BAR_VARIANT &&
          topmostHovered &&
          entry.result.datapoint === topmostHovered.datapoint
        ) {
          if (
            closest.result.type === BAR_VARIANT ||
            compareEntries(entry, closest) <= 0
          ) {
            return entry;
          }
          return closest;
        }
        const diff = compareEntries(entry, closest);
        if (diff !== 0) {
          return diff < 0 ? entry : closest;
        }
        // Tie between bar and non-bar: prefer non-bar only if within snap radius.
        if (entry.result.type !== closest.result.type) {
          const nonBar = entry.result.type !== BAR_VARIANT ? entry : closest;
          const bar = entry.result.type === BAR_VARIANT ? entry : closest;
          return euclideanDistanceToCursor(nonBar) <
            DEFAULT_DATA_POINT_RADIUS * 2
            ? nonBar
            : bar;
        }
        return closest;
      },
      undefined,
    );

    const preferred = closestEntry?.result ?? allBarsHovered.at(-1);

    let allowedBoundaries: Set<string> | undefined;
    if (allBarsHovered.length > 0) {
      allowedBoundaries = new Set(
        allBarsHovered.map(
          (b) => getBarPixelCenter(b.series, b.datapoint).boundaries,
        ),
      );
    } else if (preferred) {
      if (preferred.type === BAR_VARIANT) {
        allowedBoundaries = new Set([
          getBarPixelCenter(
            preferred.series as BarSeriesWithDatapoints,
            preferred.datapoint as BarDatapoint,
          ).boundaries,
        ]);
      } else {
        allowedBoundaries = new Set([
          `${String(preferred.datapoint.x0)}-${String(preferred.datapoint.x1)}`,
        ]);
      }
    }

    const filteredEntries = clusterEntries.filter(
      (e) =>
        allowedBoundaries === undefined || allowedBoundaries.has(e.boundaries),
    );
    const datapoints: SeriesQueryResult[] = filteredEntries.map(
      (e) => e.result,
    );

    return { preferred, datapoints };
  };
}

function findClosestClusterBinary(
  sorted: SortedEntry[],
  searchBaselinePixel: number,
  getClusterKey: (e: SortedEntry) => number | string,
): SortedEntry[] {
  if (sorted.length === 0) {
    return [];
  }

  const [left, right] = getClusterBoundariesAtPosition(
    searchBaselinePixel,
    sorted,
    (e) => e.baselinePixel,
    getClusterKey,
  );

  if (left === -1) {
    return [];
  }

  return sorted.slice(left, right + 1);
}

function collectLinearEntries(
  barSeriesData: BarSeriesWithDatapoints[],
  lineSeriesData: LineSeriesWithDatapoints[],
  areaSeriesData: AreaSeriesWithDatapoints[],
  datapointMetadataMap: MapDatapoints,
  getBarPixelCenter: GetBarPixelCenter,
  getLinearSeriesPixelCenter: GetLinearSeriesPixelCenter,
): SortedEntry[] {
  const entries: SortedEntry[] = [];

  for (const series of barSeriesData) {
    for (const datapoint of series.data) {
      if (!datapointMetadataMap.has(datapoint)) {
        return [];
      }
      if (safeGet(datapointMetadataMap, datapoint).isGap) {
        continue;
      }
      const center = getBarPixelCenter(series, datapoint);
      entries.push({
        ...center,
        result: {
          type: series.type,
          distance: 0,
          datapoint,
          series,
        } as unknown as SeriesQueryResult,
      });
    }
  }

  for (const series of [...lineSeriesData, ...areaSeriesData]) {
    for (const datapoint of series.data as (LineDatapoint | AreaDatapoint)[]) {
      if (!datapointMetadataMap.has(datapoint)) {
        return [];
      }
      if (safeGet(datapointMetadataMap, datapoint).isGap) {
        continue;
      }
      const center = getLinearSeriesPixelCenter(series, datapoint);
      entries.push({
        ...center,
        result: {
          type: series.type,
          distance: 0,
          datapoint,
          series,
        } as unknown as SeriesQueryResult,
      });
    }
  }

  return entries;
}

function filterToClosestCluster(
  entries: SortedEntry[],
  searchBaselinePixel: number,
  getClusterKey: (e: SortedEntry) => number | string,
): SortedEntry[] {
  let closestKey: number | string | undefined;
  let closestDistance = Infinity;

  for (const entry of entries) {
    const distance = Math.abs(entry.baselinePixel - searchBaselinePixel);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestKey = getClusterKey(entry);
    }
  }

  if (closestKey === undefined) {
    return [];
  }
  return entries.filter((e) => getClusterKey(e) === closestKey);
}
